import React, { useState, useRef, useCallback } from 'react';
import './App.css';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { gameData } from './data/mock';
import { shownImages } from './hooks/useLazyImage';
import mapBg from './assets/map/new_map_compressed.jpg';
import homeBg from './assets/data/home page/forest.jpg';
import missionBg from './assets/instruction_page/background_img.jpg';
import missionSilhouette from './assets/instruction_page/human_black_2.png';
import HomePage from './components/HomePage';
import IntroVideo from './components/IntroVideo';
import WorldMap from './components/WorldMap';
import LocationChallenge from './components/LocationChallenge';
import VictoryScreen from './components/VictoryScreen';
import { Toaster } from './components/ui/toaster';
import SignIn from './components/SignIn';
import MissionBriefing from './components/MissionBriefing';
import AuthGuard from './components/AuthGuard';

// Admin components
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminQuestions from './components/admin/AdminQuestions';
import AdminTestCases from './components/admin/AdminTestCases';
import AdminUsers from './components/admin/AdminUsers';
import AdminSubmissions from './components/admin/AdminSubmissions';
import AdminMappings from './components/admin/AdminMappings';
import AdminCategories from './components/admin/AdminCategories';
import AdminDryRun from './components/admin/AdminDryRun';
import AdminBulkImport from './components/admin/AdminBulkImport';
import AdminLeaderboard from './components/admin/AdminLeaderboard';
import AdminQuestionForm from './components/admin/AdminQuestionForm';
import AdminRetryRequests from './components/admin/AdminRetryRequests';
import AdminFeedbacks from './components/admin/AdminFeedbacks';
import AdminSuperSettings from './components/admin/AdminSuperSettings';

// Three-tier preload pool. Loads images in priority order so the current page's
// images get full bandwidth first, then downstream images trickle in behind.
//
// Tier 1 (immediate): home + mission briefing — the first two screens the user sees
// Tier 2 (after tier 1): map background — needed the moment user clicks "Begin Hunt"
// Tier 3 (after tier 2): all dinosaur + location images — large, load last
//
// Each onLoad also registers the src in shownImages so useLazyImage initialises
// immediately on any subsequent component mount instead of waiting for intersection.
const TIER1 = [homeBg, missionBg, missionSilhouette].filter(Boolean);
const TIER2 = [mapBg].filter(Boolean);
const TIER3 = gameData.locations.flatMap(loc => [loc.dinosaurImage, loc.backgroundImage]).filter(Boolean);

function ImagePreloadPool() {
  const [stage, setStage] = useState(1);
  const counts = useRef({ t1: 0, t2: 0 });

  const onT1Done = useCallback((src) => {
    if (src) shownImages.add(src);
    counts.current.t1 += 1;
    if (counts.current.t1 >= TIER1.length) setStage(2);
  }, []);

  const onT2Done = useCallback((src) => {
    if (src) shownImages.add(src);
    counts.current.t2 += 1;
    if (counts.current.t2 >= TIER2.length) setStage(3);
  }, []);

  const onT3Done = useCallback((src) => {
    if (src) shownImages.add(src);
  }, []);

  return (
    <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
      {TIER1.map(src => (
        <img key={src} src={src} alt="" onLoad={() => onT1Done(src)} onError={() => onT1Done(null)} />
      ))}
      {stage >= 2 && TIER2.map(src => (
        <img key={src} src={src} alt="" onLoad={() => onT2Done(src)} onError={() => onT2Done(null)} />
      ))}
      {stage >= 3 && TIER3.map(src => (
        <img key={src} src={src} alt="" onLoad={() => onT3Done(src)} onError={() => onT3Done(null)} />
      ))}
    </div>
  );
}

// Main App for game routes
function App() {
  return (
    <div className="App">
      <ImagePreloadPool />
      <HashRouter>
        <Routes>
          <Route
            path="/"
            element={(
              <AuthGuard>
                <HomePage />
              </AuthGuard>
            )}
          />
          <Route path="/signin" element={<SignIn />} />
          <Route
            path="/index.html"
            element={(
              <AuthGuard>
                <HomePage />
              </AuthGuard>
            )}
          />
          {/* <Route path="/intro" element={<AuthGuard><IntroVideo /></AuthGuard>} /> */}
          <Route
            path="/instructions"
            element={(
              <AuthGuard>
                <MissionBriefing />
              </AuthGuard>
            )}
          />
          <Route
            path="/map"
            element={(
              <AuthGuard>
                <WorldMap />
              </AuthGuard>
            )}
          />
          <Route
            path="/location/:id"
            element={(
              <AuthGuard>
                <LocationChallenge />
              </AuthGuard>
            )}
          />
          <Route
            path="/victory"
            element={(
              <AuthGuard>
                <VictoryScreen />
              </AuthGuard>
            )}
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={(
              <AuthGuard>
                <AdminLayout />
              </AuthGuard>
            )}
          >
            <Route index element={<AdminDashboard />} />
            <Route path="questions" element={<AdminQuestions />} />
            <Route path="questions/new" element={<AdminQuestionForm />} />
            <Route path="questions/:id/edit" element={<AdminQuestionForm />} />
            <Route path="testcases" element={<AdminTestCases />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="submissions" element={<AdminSubmissions />} />
            <Route path="mappings" element={<AdminMappings />} />
            <Route path="retry-requests" element={<AdminRetryRequests />} />
            <Route path="feedbacks" element={<AdminFeedbacks />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="dryrun" element={<AdminDryRun />} />
            <Route path="import" element={<AdminBulkImport />} />
            <Route path="leaderboard" element={<AdminLeaderboard />} />
            <Route path="super-settings" element={<AdminSuperSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </HashRouter>
      <Toaster />
    </div>
  );
}

// Admin App for admin routes
function Admin() {
  return (
    <div className="AdminApp">
      <HashRouter>
        <Routes>
          <Route
            path="/"
            element={(
              <AuthGuard>
                <AdminLayout />
              </AuthGuard>
            )}
          >
            <Route index element={<AdminDashboard />} />
            <Route path="leaderboard" element={<AdminLeaderboard />} />
            <Route path="questions" element={<AdminQuestions />} />
            <Route path="questions/new" element={<AdminQuestionForm />} />
            <Route path="questions/:id/edit" element={<AdminQuestionForm />} />
            <Route path="testcases" element={<AdminTestCases />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="submissions" element={<AdminSubmissions />} />
            <Route path="mappings" element={<AdminMappings />} />
            <Route path="retry-requests" element={<AdminRetryRequests />} />
            <Route path="feedbacks" element={<AdminFeedbacks />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="dryrun" element={<AdminDryRun />} />
            <Route path="import" element={<AdminBulkImport />} />
            <Route path="super-settings" element={<AdminSuperSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </HashRouter>
      <Toaster />
    </div>
  );
}

export default App;
export { Admin };
